import './index.css';
import type { ThemeConfig } from '../../themes/types';
import { IBimComponent } from '../../types/component';
import { localeManager, t } from '../../services/locale';
import { themeManager } from '../../services/theme';
import type { HelpPanelOptions, HelpTab } from './types';

export class HelpPanel implements IBimComponent {
    public element!: HTMLElement;
    private overlay!: HTMLElement;
    private container!: HTMLElement;
    private options: HelpPanelOptions;

    private viewTabBtn!: HTMLButtonElement;
    private fpsTabBtn!: HTMLButtonElement;
    private viewContent!: HTMLElement;
    private fpsContent!: HTMLElement;
    private confirmBtn!: HTMLButtonElement;
    private dontRemindBtn!: HTMLButtonElement;

    private unsubscribeLocale: (() => void) | null = null;
    private unsubscribeTheme: (() => void) | null = null;

    private static readonly STORAGE_KEY = 'bim-engine:help-panel:hidden';

    constructor(options: HelpPanelOptions = {}) {
        this.options = options;
    }

    public init(): void {
        this.element = this.createPanel();
        this.unsubscribeLocale = localeManager.subscribe(() => this.setLocales());
        this.unsubscribeTheme = themeManager.subscribe((theme) => this.setTheme(theme));

        this.setLocales();
        this.setTheme(themeManager.getTheme());
    }

    public show(): void {
        this.overlay.classList.add('active');
    }

    public hide(): void {
        this.overlay.classList.remove('active');
    }

    public isVisible(): boolean {
        return this.overlay.classList.contains('active');
    }

    public shouldAutoShow(): boolean {
        const storageKey = this.options.storageKey ?? HelpPanel.STORAGE_KEY;
        return localStorage.getItem(storageKey) !== 'true';
    }

    private createPanel(): HTMLElement {
        const wrapper = document.createElement('div');
        wrapper.className = 'bim-help-panel-wrapper';

        this.overlay = document.createElement('div');
        this.overlay.className = 'bim-help-panel-overlay';
        this.overlay.addEventListener('click', (e) => {
            if (e.target === this.overlay) {
                this.hide();
            }
        });

        this.container = document.createElement('div');
        this.container.className = 'bim-help-panel-container';

        const header = this.createHeader();
        const body = this.createBody();
        const footer = this.createFooter();

        this.container.appendChild(header);
        this.container.appendChild(body);
        this.container.appendChild(footer);

        this.overlay.appendChild(this.container);
        wrapper.appendChild(this.overlay);

        return wrapper;
    }

    private createHeader(): HTMLElement {
        const header = document.createElement('div');
        header.className = 'bim-help-panel-header';

        this.viewTabBtn = document.createElement('button');
        this.viewTabBtn.className = 'bim-help-panel-tab-btn active';
        this.viewTabBtn.addEventListener('click', () => this.switchTab('view'));

        this.fpsTabBtn = document.createElement('button');
        this.fpsTabBtn.className = 'bim-help-panel-tab-btn';
        this.fpsTabBtn.addEventListener('click', () => this.switchTab('fps'));

        header.appendChild(this.viewTabBtn);
        header.appendChild(this.fpsTabBtn);

        return header;
    }

    private createBody(): HTMLElement {
        const body = document.createElement('div');
        body.className = 'bim-help-panel-body';

        this.viewContent = this.createViewTabContent();
        this.fpsContent = this.createFpsTabContent();

        body.appendChild(this.viewContent);
        body.appendChild(this.fpsContent);

        return body;
    }

    private createViewTabContent(): HTMLElement {
        const content = document.createElement('div');
        content.className = 'bim-help-panel-tab-content active';
        content.dataset.tab = 'view';

        // 旋转视图 - 带鼠标左键图标
        content.appendChild(this.createMouseCard('rotateView', 'left'));

        // 平移视图 - 带鼠标右键图标
        content.appendChild(this.createMouseCard('panView', 'right'));

        // 缩放视图 - 带鼠标滚轮图标
        content.appendChild(this.createMouseCard('zoomView', 'wheel'));

        // 框选构件
        const boxSelectCard = this.createCard('boxSelect', 'ctrlLeftDrag');
        content.appendChild(boxSelectCard);

        // 构件选择 - 占2列
        const multiSelectCard = this.createMultiSelectCard();
        content.appendChild(multiSelectCard);

        // 视图复位
        const resetCard = this.createCard('resetView', 'doubleClickBlankShort');
        content.appendChild(resetCard);

        // 旋转点规则 - 占2列
        const ruleCard = this.createRuleCard();
        content.appendChild(ruleCard);

        return content;
    }

    private createMouseCard(titleKey: string, type: 'left' | 'right' | 'wheel'): HTMLElement {
        const card = document.createElement('div');
        card.className = 'bim-help-panel-card';

        const title = document.createElement('div');
        title.className = 'bim-help-panel-card-title';
        title.dataset.localeKey = `help.${titleKey}`;

        const iconWrapper = document.createElement('div');
        iconWrapper.className = 'bim-help-panel-control-row';

        const mouseBox = document.createElement('div');
        mouseBox.className = 'bim-help-panel-mouse-box';

        if (type === 'left') {
            const leftBtn = document.createElement('div');
            leftBtn.className = 'bim-help-panel-mouse-left';
            mouseBox.appendChild(leftBtn);
        } else if (type === 'right') {
            const rightBtn = document.createElement('div');
            rightBtn.className = 'bim-help-panel-mouse-right';
            mouseBox.appendChild(rightBtn);
        } else if (type === 'wheel') {
            const wheel = document.createElement('div');
            wheel.className = 'bim-help-panel-mouse-wheel';
            mouseBox.appendChild(wheel);
        }

        iconWrapper.appendChild(mouseBox);

        const desc = document.createElement('div');
        desc.className = 'bim-help-panel-rule-desc';
        desc.dataset.localeKey = `help.${type === 'left' ? 'mouseLeftDragShort' : type === 'right' ? 'mouseRightDragShort' : 'mouseWheelShort'}`;

        card.appendChild(title);
        card.appendChild(iconWrapper);
        card.appendChild(desc);

        return card;
    }

    private createMultiSelectCard(): HTMLElement {
        const card = document.createElement('div');
        card.className = 'bim-help-panel-card bim-help-panel-card-span-2';

        const title = document.createElement('div');
        title.className = 'bim-help-panel-card-title';
        title.dataset.localeKey = 'help.componentSelect';

        const row = document.createElement('div');
        row.className = 'bim-help-panel-control-row';
        row.style.gap = '40px';

        const leftCol = document.createElement('div');
        leftCol.style.textAlign = 'center';
        const leftKeys = document.createElement('div');
        leftKeys.className = 'bim-help-panel-control-row';
        leftKeys.innerHTML = '<kbd class="bim-help-panel-kbd">Ctrl</kbd> <span class="bim-help-panel-bold">+</span> <span class="bim-help-panel-bold" data-locale-key="help.click"></span>';
        const leftDesc = document.createElement('div');
        leftDesc.className = 'bim-help-panel-rule-desc';
        leftDesc.dataset.localeKey = 'help.addSelect';
        leftCol.appendChild(leftKeys);
        leftCol.appendChild(leftDesc);

        const rightCol = document.createElement('div');
        rightCol.style.textAlign = 'center';
        const rightKeys = document.createElement('div');
        rightKeys.className = 'bim-help-panel-control-row';
        rightKeys.innerHTML = '<kbd class="bim-help-panel-kbd">Shift</kbd> <span class="bim-help-panel-bold">+</span> <span class="bim-help-panel-bold" data-locale-key="help.click"></span>';
        const rightDesc = document.createElement('div');
        rightDesc.className = 'bim-help-panel-rule-desc';
        rightDesc.dataset.localeKey = 'help.removeSelect';
        rightCol.appendChild(rightKeys);
        rightCol.appendChild(rightDesc);

        row.appendChild(leftCol);
        row.appendChild(rightCol);

        card.appendChild(title);
        card.appendChild(row);

        return card;
    }

    private createRuleCard(): HTMLElement {
        const card = document.createElement('div');
        card.className = 'bim-help-panel-card bim-help-panel-card-span-2';

        const title = document.createElement('div');
        title.className = 'bim-help-panel-card-title';
        title.dataset.localeKey = 'help.rotateRuleTitle';

        const desc = document.createElement('div');
        desc.className = 'bim-help-panel-rule-desc';
        desc.style.textAlign = 'left';
        desc.style.width = '100%';

        const line1 = document.createElement('div');
        line1.style.marginBottom = '6px';
        const bullet1 = document.createElement('span');
        bullet1.className = 'bim-help-panel-bold';
        bullet1.textContent = '• ';
        const label1 = document.createElement('span');
        label1.className = 'bim-help-panel-bold';
        label1.dataset.localeKey = 'help.pointToComponent';
        const text1 = document.createElement('span');
        text1.dataset.localeKey = 'help.pointToComponentDesc';
        line1.appendChild(bullet1);
        line1.appendChild(label1);
        line1.appendChild(text1);

        const line2 = document.createElement('div');
        const bullet2 = document.createElement('span');
        bullet2.className = 'bim-help-panel-bold';
        bullet2.textContent = '• ';
        const label2 = document.createElement('span');
        label2.className = 'bim-help-panel-bold';
        label2.dataset.localeKey = 'help.pointToBlank';
        const text2 = document.createElement('span');
        text2.dataset.localeKey = 'help.pointToBlankDesc';
        line2.appendChild(bullet2);
        line2.appendChild(label2);
        line2.appendChild(text2);

        desc.appendChild(line1);
        desc.appendChild(line2);

        card.appendChild(title);
        card.appendChild(desc);

        return card;
    }

    private createFpsTabContent(): HTMLElement {
        const content = document.createElement('div');
        content.className = 'bim-help-panel-tab-content';
        content.dataset.tab = 'fps';

        // 漫游移动 - 占2列
        const roamCard = document.createElement('div');
        roamCard.className = 'bim-help-panel-card bim-help-panel-card-span-2';

        const roamTitle = document.createElement('div');
        roamTitle.className = 'bim-help-panel-card-title';
        roamTitle.dataset.localeKey = 'help.roamMove';

        const roamContent = document.createElement('div');
        roamContent.className = 'bim-help-panel-control-row';
        roamContent.style.gap = '48px';
        roamContent.innerHTML = `
            <div class="bim-help-panel-wasd-group">
                <div class="bim-help-panel-kbd-row"><kbd class="bim-help-panel-kbd">W</kbd></div>
                <div class="bim-help-panel-kbd-row">
                    <kbd class="bim-help-panel-kbd">A</kbd>
                    <kbd class="bim-help-panel-kbd">S</kbd>
                    <kbd class="bim-help-panel-kbd">D</kbd>
                </div>
            </div>
        `;

        const roamDesc = document.createElement('div');
        roamDesc.className = 'bim-help-panel-rule-desc';
        roamDesc.dataset.localeKey = 'help.roamMoveDesc';

        roamCard.appendChild(roamTitle);
        roamCard.appendChild(roamContent);
        roamCard.appendChild(roamDesc);
        content.appendChild(roamCard);

        // 加速移动
        content.appendChild(this.createCard('speedUp', 'shiftArrow'));

        // 升降高度
        content.appendChild(this.createCard('upDown', 'qeKey', 'landOnFloor'));

        // 环视四周 - 带鼠标图标
        const lookCard = document.createElement('div');
        lookCard.className = 'bim-help-panel-card';

        const lookTitle = document.createElement('div');
        lookTitle.className = 'bim-help-panel-card-title';
        lookTitle.dataset.localeKey = 'help.lookAround';

        const lookIcon = document.createElement('div');
        lookIcon.className = 'bim-help-panel-control-row';
        const lookMouse = document.createElement('div');
        lookMouse.className = 'bim-help-panel-mouse-box';
        const lookLeft = document.createElement('div');
        lookLeft.className = 'bim-help-panel-mouse-left';
        lookMouse.appendChild(lookLeft);
        lookIcon.appendChild(lookMouse);

        const lookDesc = document.createElement('div');
        lookDesc.className = 'bim-help-panel-rule-desc';
        lookDesc.dataset.localeKey = 'help.lookAroundDesc';

        lookCard.appendChild(lookTitle);
        lookCard.appendChild(lookIcon);
        lookCard.appendChild(lookDesc);
        content.appendChild(lookCard);

        // 位置传送 - 带传送图标
        const tpCard = document.createElement('div');
        tpCard.className = 'bim-help-panel-card';

        const tpTitle = document.createElement('div');
        tpTitle.className = 'bim-help-panel-card-title';
        tpTitle.dataset.localeKey = 'help.teleport';

        const tpIcon = document.createElement('div');
        tpIcon.className = 'bim-help-panel-control-row';
        const tpIconInner = document.createElement('div');
        tpIconInner.className = 'bim-help-panel-teleport-icon';
        const tpInnerDot = document.createElement('div');
        tpInnerDot.className = 'bim-help-panel-teleport-inner';
        tpIconInner.appendChild(tpInnerDot);
        tpIcon.appendChild(tpIconInner);

        const tpDesc = document.createElement('div');
        tpDesc.className = 'bim-help-panel-rule-desc';
        tpDesc.dataset.localeKey = 'help.teleportDesc';

        tpCard.appendChild(tpTitle);
        tpCard.appendChild(tpIcon);
        tpCard.appendChild(tpDesc);
        content.appendChild(tpCard);

        return content;
    }

    private createCard(titleKey: string, contentKey: string, descKey?: string): HTMLElement {
        const card = document.createElement('div');
        card.className = 'bim-help-panel-card';

        const title = document.createElement('div');
        title.className = 'bim-help-panel-card-title';
        title.dataset.localeKey = `help.${titleKey}`;

        const content = document.createElement('div');
        content.className = 'bim-help-panel-control-row';
        content.dataset.localeKey = `help.${contentKey}`;

        card.appendChild(title);
        card.appendChild(content);

        if (descKey) {
            const desc = document.createElement('div');
            desc.className = 'bim-help-panel-rule-desc';
            desc.dataset.localeKey = `help.${descKey}`;
            card.appendChild(desc);
        }

        return card;
    }

    private createFooter(): HTMLElement {
        const footer = document.createElement('div');
        footer.className = 'bim-help-panel-footer';

        this.confirmBtn = document.createElement('button');
        this.confirmBtn.className = 'bim-help-panel-btn bim-help-panel-btn-primary';
        this.confirmBtn.addEventListener('click', () => this.hide());

        this.dontRemindBtn = document.createElement('button');
        this.dontRemindBtn.className = 'bim-help-panel-btn bim-help-panel-btn-secondary';
        this.dontRemindBtn.addEventListener('click', () => this.dontRemind());

        footer.appendChild(this.confirmBtn);
        footer.appendChild(this.dontRemindBtn);

        return footer;
    }

    private switchTab(tab: HelpTab): void {
        this.viewTabBtn.classList.toggle('active', tab === 'view');
        this.fpsTabBtn.classList.toggle('active', tab === 'fps');

        this.viewContent.classList.toggle('active', tab === 'view');
        this.fpsContent.classList.toggle('active', tab === 'fps');
    }

    private dontRemind(): void {
        const storageKey = this.options.storageKey ?? HelpPanel.STORAGE_KEY;
        localStorage.setItem(storageKey, 'true');
        this.hide();
        this.options.onDontRemind?.();
    }

    public setLocales(): void {
        this.viewTabBtn.textContent = t('help.engineControl');
        this.fpsTabBtn.textContent = t('help.fpsControl');
        this.confirmBtn.textContent = t('help.gotIt');
        this.dontRemindBtn.textContent = t('help.dontRemind');

        this.element.querySelectorAll('[data-locale-key]').forEach((el) => {
            const key = el.getAttribute('data-locale-key');
            if (key) {
                el.textContent = t(key);
            }
        });
    }

    public setTheme(theme: ThemeConfig): void {
        this.element.style.setProperty('--bim-bg-overlay', 'rgba(0, 0, 0, 0.5)');
        this.element.style.setProperty('--bim-panel-bg', theme.bgElevated);
        this.element.style.setProperty('--bim-panel-border', theme.panelBorder);
        this.element.style.setProperty('--bim-shadow-lg', theme.shadowLg);
        this.element.style.setProperty('--bim-text-primary', theme.textPrimary);
        this.element.style.setProperty('--bim-text-secondary', theme.textSecondary);
        this.element.style.setProperty('--bim-text-tertiary', theme.textTertiary);
        this.element.style.setProperty('--bim-primary', theme.primary);
        this.element.style.setProperty('--bim-primary-hover', theme.primaryHover);
        this.element.style.setProperty('--bim-text-inverse', theme.textInverse);
        this.element.style.setProperty('--bim-divider', theme.divider);
        this.element.style.setProperty('--bim-border-default', theme.borderDefault);
        this.element.style.setProperty('--bim-border-subtle', theme.borderSubtle);
        this.element.style.setProperty('--bim-component-bg', theme.componentBg);
        this.element.style.setProperty('--bim-component-bg-hover', theme.componentBgHover);
        this.element.style.setProperty('--bim-bg-inset', theme.bgInset);
        this.element.style.setProperty('--bim-scrollbar-thumb', theme.scrollbarThumb);
        this.element.style.setProperty('--bim-scrollbar-thumb-hover', theme.scrollbarThumbHover);
    }

    public destroy(): void {
        if (this.unsubscribeLocale) {
            this.unsubscribeLocale();
            this.unsubscribeLocale = null;
        }
        if (this.unsubscribeTheme) {
            this.unsubscribeTheme();
            this.unsubscribeTheme = null;
        }
        if (this.overlay) {
            this.overlay.remove();
        }
    }
}
